<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div style="width: 100%; max-width: 1200px;">

      <!-- Filters header -->
      <div class="table-header-filters pa-4 mb-6">
        <v-row dense align="center">

          <!-- Period pills -->
          <v-col cols="12" sm="auto">
            <div class="period-pills">
              <button
                v-for="opt in periodOptions"
                :key="opt.value"
                class="period-pill"
                :class="{ active: period === opt.value }"
                @click="period = opt.value"
              >
                <v-icon x-small class="mr-1">{{ opt.icon }}</v-icon>
                {{ opt.label }}
              </button>
            </div>
          </v-col>

          <!-- Custom date range -->
          <v-col v-if="period === 'custom'" cols="12" sm="auto" class="d-flex align-center" style="gap: 6px;">
            <v-menu v-model="menuFrom" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-start</v-icon>
                  {{ formatDate(dateFrom) }}
                </button>
              </template>
              <v-date-picker v-model="dateFrom" no-title scrollable @input="menuFrom = false; fetchData()" />
            </v-menu>
            <span class="date-pill-sep">—</span>
            <v-menu v-model="menuTo" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-end</v-icon>
                  {{ formatDate(dateTo) }}
                </button>
              </template>
              <v-date-picker v-model="dateTo" no-title scrollable @input="menuTo = false; fetchData()" />
            </v-menu>
          </v-col>

          <!-- Right controls -->
          <v-col cols="auto" class="d-flex align-center ml-auto" style="gap: 8px;">

            <!-- Seller multi-select -->
            <v-select
              v-if="sellers.length > 0"
              v-model="selectedSellers"
              :items="sellerItems"
              item-text="name"
              item-value="id"
              multiple
              dense
              outlined
              hide-details
              :placeholder="`Svi (${sellers.length})`"
              style="max-width: 220px;"
              class="seller-select"
            >
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="seller-chip">
                  <v-icon x-small class="mr-1" color="#00838F">mdi-account</v-icon>
                  {{ selectedSellers.length === 1 ? item.name : `${selectedSellers.length} odabrano` }}
                </span>
              </template>
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on" dense>
                  <v-list-item-action class="mr-2">
                    <v-checkbox
                      :input-value="attrs.inputValue"
                      color="#00BCD4"
                      dense
                      hide-details
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title style="font-size: 13px;">{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle style="font-size: 11px;">
                      {{ formatEur(item.totalEur) }} · {{ item.count }} rač.
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-avatar size="10" style="margin: 0;">
                    <div :style="`width: 10px; height: 10px; border-radius: 50%; background: ${item.color}`" />
                  </v-list-item-avatar>
                </v-list-item>
              </template>
            </v-select>

            <!-- Promet / Br. računa toggle -->
            <v-switch
              v-model="showRevenue"
              dense
              hide-details
              color="cyan darken-1"
              class="mt-0 pt-0"
              :label="showRevenue ? 'Promet' : 'Br. računa'"
            />

            <!-- Close -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="{ name: `${targetModifier}.all-reports.reportMenu` }" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="cyan darken-1" size="48" />
        <div class="mt-4 grey--text">Učitavanje podataka...</div>
      </div>

      <template v-else-if="visibleSellers.length === 0">
        <div class="text-center py-12 grey--text">Nema podataka za odabrani period</div>
      </template>

      <template v-else>

        <!-- Bar chart -->
        <v-card outlined rounded="lg" class="pa-6 mb-6">
          <div class="section-title mb-4">
            Promet po zaposleniku — {{ periodLabel }}
          </div>
          <apexchart
            type="bar"
            :height="Math.max(180, visibleSellers.length * 48)"
            :options="chartOptions"
            :series="chartSeries"
          />
        </v-card>

        <!-- Table -->
        <v-card outlined rounded="lg" class="pa-6">
          <div class="section-title mb-4">Tablica zaposlenika</div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th style="width: 16px;"></th>
                <th>Zaposlenik</th>
                <th class="text-right">Promet</th>
                <th class="text-right">Br. računa</th>
                <th class="text-right">Prosjek</th>
                <th class="text-right">Udio</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(s, i) in visibleSellers" :key="s.id">
                <td>
                  <div :style="`width: 10px; height: 10px; border-radius: 50%; background: ${sellerColor(i)}`" />
                </td>
                <td>
                  <span class="font-weight-medium">{{ s.name }}</span>
                </td>
                <td class="text-right" :style="`color: ${sellerColor(i)}; font-weight: 600;`">
                  {{ formatEur(s.totalEur) }}
                </td>
                <td class="text-right grey--text">{{ s.count }}</td>
                <td class="text-right grey--text">{{ formatEur(s.avgEur) }}</td>
                <td class="text-right">
                  <span class="share-pill" :style="`background: ${sellerColor(i)}1A; color: ${sellerColor(i)};`">
                    {{ sharePercent(s) }}%
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr style="background: #F8FAFC; border-top: 2px solid #E0E6ED;">
                <td></td>
                <td><strong>Ukupno</strong></td>
                <td class="text-right"><strong>{{ formatEur(totalEur) }}</strong></td>
                <td class="text-right"><strong>{{ totalCount }}</strong></td>
                <td class="text-right grey--text">{{ totalCount > 0 ? formatEur(totalEur / totalCount) : '—' }}</td>
                <td></td>
              </tr>
            </tfoot>
          </v-simple-table>
        </v-card>

      </template>
    </div>
  </v-container>
</template>

<script>
import { getFirestore, collection, query, where, getDocs, getAggregateFromServer, sum, count } from 'firebase/firestore'
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import VueApexCharts from 'vue-apexcharts'
import Vue from 'vue'

Vue.use(VueApexCharts)
Vue.component('apexchart', VueApexCharts)

const today = new Date().toISOString().substr(0, 10)

const SELLER_COLORS = [
  '#00B3BE', '#6154A4', '#3989C9', '#4CAF50',
  '#FF9800', '#E91E63', '#00838F', '#9C27B0'
]

export default {
  name: 'PromZaposlenika',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data () {
    return {
      margin: '0px',
      targetModifier: '',
      loading: false,
      period: 'today',
      showRevenue: true,
      menuFrom: false,
      menuTo: false,
      dateFrom: today,
      dateTo: today,
      periodOptions: [
        { value: 'today', label: 'Danas', icon: 'mdi-calendar-today' },
        { value: 'yesterday', label: 'Jučer', icon: 'mdi-calendar-arrow-left' },
        { value: 'week', label: 'Ovaj tjedan', icon: 'mdi-calendar-week' },
        { value: 'custom', label: 'Prilagođeno', icon: 'mdi-calendar-edit' }
      ],
      sellers: [],
      selectedSellers: []
    }
  },

  computed: {
    periodLabel () {
      const m = this.periodOptions.find(o => o.value === this.period)
      if (this.period === 'custom') return `${this.formatDate(this.dateFrom)} — ${this.formatDate(this.dateTo)}`
      return m ? m.label : ''
    },

    dateRange () {
      const now = new Date()
      const todaySec = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000)
      if (this.period === 'today') return { from: todaySec, to: todaySec + 86399 }
      if (this.period === 'yesterday') return { from: todaySec - 86400, to: todaySec - 1 }
      if (this.period === 'custom') {
        const from = Math.floor(new Date(this.dateFrom).getTime() / 1000)
        const to = Math.floor(new Date(this.dateTo).getTime() / 1000) + 86399
        return { from, to }
      }
      // week
      const day = now.getDay() || 7
      const mondaySec = todaySec - (day - 1) * 86400
      return { from: mondaySec, to: todaySec + 86399 }
    },

    sellerItems () {
      return this.sellers.map((s, i) => ({
        id: s.id,
        name: s.name,
        totalEur: s.totalEur,
        count: s.count,
        color: this.sellerColor(i)
      }))
    },

    visibleSellers () {
      if (!this.selectedSellers.length) return this.sellers
      return this.sellers.filter(s => this.selectedSellers.includes(s.id))
    },

    totalEur () {
      return this.visibleSellers.reduce((acc, s) => acc + s.totalEur, 0)
    },
    totalCount () {
      return this.visibleSellers.reduce((acc, s) => acc + s.count, 0)
    },

    chartSeries () {
      const data = this.visibleSellers.map(s =>
        this.showRevenue
          ? parseFloat(s.totalEur.toFixed(2))
          : s.count
      )
      return [{ name: this.showRevenue ? 'Promet (EUR)' : 'Broj računa', data }]
    },

    chartOptions () {
      const visibleColors = this.visibleSellers.map((_, i) => {
        const originalIndex = this.sellers.findIndex(s => s.id === this.visibleSellers[i].id)
        return this.sellerColor(originalIndex)
      })
      const isRevenue = this.showRevenue
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Raleway, sans-serif' },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '60%',
            distributed: true,
            borderRadius: 4
          }
        },
        colors: visibleColors,
        xaxis: {
          categories: this.visibleSellers.map(s => s.name),
          labels: {
            formatter: isRevenue ? v => `€${Number(v).toFixed(0)}` : v => String(Math.round(v))
          }
        },
        yaxis: {
          labels: { style: { fontSize: '12px', fontWeight: 500 } }
        },
        dataLabels: {
          enabled: true,
          formatter: isRevenue
            ? v => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
            : v => `${v} rač.`,
          style: { fontSize: '11px', colors: ['#fff'] }
        },
        legend: { show: false },
        tooltip: {
          y: {
            formatter: isRevenue
              ? v => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
              : v => `${v} računa`
          }
        },
        grid: { borderColor: '#E0E6ED', strokeDashArray: 3, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } }
      }
    }
  },

  watch: {
    period (val) {
      if (val !== 'custom') this.fetchData()
    }
  },

  async created () {
    this.margin = state.getMargin()
    const sideNav = this.$route.meta?.sideNav
    if (sideNav === 'Company') this.targetModifier = 'company'
    else if (sideNav === 'PointOfSale') this.targetModifier = 'pointOfSale'
    else if (sideNav === 'CashRegister') this.targetModifier = 'cashRegister'
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    await this.fetchData()
  },

  beforeDestroy () {
    EventBus.$off('set-mini')
  },

  methods: {
    toTitleCase (s) {
      return s.split(' ').map(w => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w).join(' ')
    },

    sellerColor (index) {
      return SELLER_COLORS[index % SELLER_COLORS.length]
    },

    sharePercent (seller) {
      if (!this.totalEur) return '0.0'
      const val = this.showRevenue ? seller.totalEur / this.totalEur : seller.count / this.totalCount
      return (val * 100).toFixed(1)
    },

    formatEur (v) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
    },

    formatDate (d) {
      if (!d) return ''
      const [y, m, day] = d.split('-')
      return `${day}.${m}.${y}`
    },

    async fetchData () {
      this.loading = true
      try {
        const companyId = state.getCurrentCompany().id
        const db = getFirestore()

        // Step 1: seller list from company users
        const usersSnap = await getDocs(collection(db, `companies/${companyId}/users`))
        const sellers = usersSnap.docs
          .map(doc => {
            const u = doc.data().user
            if (!u?.id) return null
            const name = `${u.name || ''} ${u.surname || ''}`.trim()
            return { id: u.id, name: this.toTitleCase(name || u.id) }
          })
          .filter(Boolean)

        if (!sellers.length) {
          this.sellers = []
          return
        }

        // Step 2: one aggregate query per seller using seller_uid
        // Requires composite index: seller_uid (asc) + issue_date_time (asc)
        const col = collection(db, `receipts/${companyId}/receipts`)
        const { from, to } = this.dateRange

        const results = await Promise.all(
          sellers.map(seller =>
            getAggregateFromServer(
              query(col,
                where('seller_uid', '==', seller.id),
                where('issue_date_time', '>=', from),
                where('issue_date_time', '<=', to)
              ),
              { totalCents: sum('total'), cnt: count() }
            )
              .then(snap => ({
                id: seller.id,
                name: seller.name,
                totalCents: snap.data().totalCents || 0,
                count: snap.data().cnt || 0
              }))
              .catch(err => {
                console.warn('PromZaposlenika aggregate error:', seller.id, err?.message)
                return { id: seller.id, name: seller.name, totalCents: 0, count: 0 }
              })
          )
        )

        this.sellers = results
          .filter(s => s.count > 0)
          .map(s => ({ ...s, totalEur: s.totalCents / 100, avgEur: s.count > 0 ? s.totalCents / 100 / s.count : 0 }))
          .sort((a, b) => b.totalEur - a.totalEur)

        const validIds = new Set(this.sellers.map(s => s.id))
        this.selectedSellers = this.selectedSellers.filter(id => validIds.has(id))
      } catch (err) {
        console.error('PromZaposlenika:', err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.close-btn {
  width: 36px !important; height: 36px !important; min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}
.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}
.table-header-filters {
  position: sticky; top: 64px; z-index: 10;
  background: #fff; border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.period-pills { display: flex; flex-wrap: wrap; gap: 6px; }
.period-pill {
  display: inline-flex; align-items: center;
  padding: 6px 14px; border-radius: 20px;
  border: 1.5px solid #E0E6ED; background: #fff;
  font-size: 13px; font-weight: 500; color: #6B7280;
  cursor: pointer; transition: all 0.15s ease; white-space: nowrap; outline: none;
}
.period-pill:hover { border-color: #00BCD4; color: #00838F; background: rgba(0,188,212,0.05); }
.period-pill.active { border-color: #00BCD4; background: rgba(0,188,212,0.12); color: #00838F; font-weight: 600; }
.date-pill { font-variant-numeric: tabular-nums; letter-spacing: 0.3px; }
.date-pill-sep { font-size: 13px; color: #9CA3AF; font-weight: 500; }
.section-title { font-size: 16px; font-weight: 700; color: #2d3748; }
.seller-chip { font-size: 13px; font-weight: 500; color: #00838F; display: flex; align-items: center; }
.seller-select >>> .v-input__control { min-height: 36px !important; }
.seller-select >>> fieldset { border-color: #E0E6ED !important; border-radius: 20px !important; }
.seller-select >>> .v-select__selections { padding: 0 8px !important; }
.share-pill {
  display: inline-block; padding: 2px 8px;
  border-radius: 12px; font-size: 11px; font-weight: 600;
}
</style>
